'use client';

import {
  Button,
  ButtonGroup,
  Card,
  CardAside,
  CardContent,
  CardHeader,
  EmptyState,
  Flex,
  Heading,
  LayoutContent,
} from '@/_components';
import { ClientChatList } from './components/ClientChatList';
import { useContext, useMemo, type FC } from 'react';
import { useParams, usePathname } from 'next/navigation';
import { ClientConversationsContext } from '@/_contexts/client/ClientConversationsProvider';
import { CompanyContext } from '@/_contexts/admin/CompanyProvider';
import { UserContext } from '@/_contexts/UserProvider';
import type { ClientMessagesWrapperProps } from './ClientMessagesWrapper.types';

export const ClientMessagesWrapper: FC<ClientMessagesWrapperProps> = ({
  children,
  isAdvisor,
}) => {
  const { isLoading, conversations } = useContext(ClientConversationsContext);
  const { selectedCompany, onToggleAddClient } = useContext(CompanyContext);
  const { isLoading: isUserLoading } = useContext(UserContext);

  const pathname = usePathname();
  const { conversationId } = useParams();

  const isClientAside = pathname.startsWith('/inbox') && !conversationId;
  const isAsideNavigation: boolean = pathname === '/messages' || isClientAside;

  const shouldShowAllConversations: boolean = useMemo(
    () =>
      !isUserLoading &&
      !isLoading &&
      isAdvisor &&
      !selectedCompany?.id &&
      pathname === '/messages',
    [isUserLoading, isLoading, isAdvisor, selectedCompany?.id, pathname],
  );
  const isEmpty: boolean = !isLoading && conversations?.length === 0;

  return (
    <>
      <LayoutContent>
        <Card isRow={true}>
          {isAdvisor && (
            <CardAside isNavigationControl={isAsideNavigation}>
              <Flex isColumn={true}>
                <CardHeader>
                  <Flex isBetween={true} gap={4} padding={4} isYCentered={true}>
                    <Heading level={4}>Messages</Heading>
                  </Flex>
                </CardHeader>
                <ClientChatList
                  isAdvisor={isAdvisor}
                  shouldShowAllConversations={shouldShowAllConversations}
                  showUnreadOnly={false}
                />
              </Flex>
            </CardAside>
          )}
          <CardContent isFullHeight={true} withAside={isAdvisor}>
            {isEmpty && isAdvisor && !conversations?.length ? (
              <EmptyState
                description="No available conversations found"
                icon="message"
              >
                <ButtonGroup align="center">
                  <Button
                    value="Add Entity"
                    iconLeft="plus"
                    size="small"
                    intent="secondary"
                    onClick={onToggleAddClient}
                  />
                </ButtonGroup>
              </EmptyState>
            ) : (
              children
            )}
          </CardContent>
        </Card>
      </LayoutContent>
    </>
  );
};
